import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import PageLayout from "@/components/PageLayout";

const Terms = () => {
  return (
    <PageLayout
      seo={{
        title: "Terms of Service - Eunice",
        description: "Terms and conditions for using Eunice travel letter service.",
        canonical: "/terms",
      }}
    >
      <article>
        <header className="mb-10 opacity-0 animate-fade-up">
          <h1 className="font-serif text-3xl md:text-4xl text-foreground mb-3">
            Terms of Service
          </h1>
          <p className="text-sm text-muted-foreground font-sans">
            Last updated: January 24, 2025
          </p>
        </header>

        <div className="space-y-8 text-charcoal-light leading-relaxed font-sans">
          <section className="opacity-0 animate-fade-up animation-delay-100">
            <p>
              Welcome to Eunice. By purchasing a letter, you agree to these terms. 
              We've written them in plain language because we believe you deserve to 
              understand what you're agreeing to.
            </p>
          </section>

          <section className="opacity-0 animate-fade-up animation-delay-200">
            <h2 className="font-serif text-xl text-foreground mb-3">What We Offer</h2>
            <p className="mb-3">
              Eunice provides AI-generated personalized travel letters. Each letter is created 
              using artificial intelligence (specifically, Anthropic's Claude) and delivered 
              to your email within 24 hours of purchase.
            </p>
            <p className="p-4 bg-cream-dark/50 rounded-lg border border-tan/30 italic">
              <strong className="not-italic text-foreground">Important:</strong> Our letters are meant to inspire and 
              inform, not replace professional travel advice. They're a creative starting point, 
              not a guarantee of accuracy.
            </p>
          </section>

          <section className="opacity-0 animate-fade-up animation-delay-300">
            <h2 className="font-serif text-xl text-foreground mb-3">Purchase Terms</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>Each letter costs $1.99 USD (one-time purchase)</li>
              <li>Payment is processed securely through Stripe</li>
              <li>Your letter will be delivered to the email you provide within 24 hours</li>
              <li>All sales are final—since we deliver digital goods immediately, we cannot offer refunds</li>
            </ul>
          </section>

          <section className="opacity-0 animate-fade-up animation-delay-300">
            <h2 className="font-serif text-xl text-foreground mb-3">About AI-Generated Content</h2>
            <p className="mb-3">
              Let's be clear: Eunice is a character, and her letters are generated by AI. While we 
              work hard to make the content helpful and interesting, you should know:
            </p>
            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li>Information may be outdated (restaurants close, neighborhoods change)</li>
              <li>Details may be inaccurate (AI can make mistakes)</li>
              <li>Recommendations are general, not tailored to your specific needs</li>
              <li>Always verify important details before traveling</li>
            </ul>
            <p className="p-4 bg-cream-dark/50 rounded-lg border border-tan/30">
              Think of Eunice's letters like advice from a well-traveled friend—interesting and 
              potentially useful, but not something to follow blindly. Do your own research too.
            </p>
          </section>

          <section className="opacity-0 animate-fade-up animation-delay-300">
            <h2 className="font-serif text-xl text-foreground mb-3">Your Responsibilities</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>Provide a valid email address where we can deliver your letter</li>
              <li>Verify any recommendations before making travel decisions</li>
              <li>Use the content at your own risk</li>
              <li>Be at least 18 years old, or have parental permission</li>
            </ul>
          </section>

          <section className="opacity-0 animate-fade-up animation-delay-300">
            <h2 className="font-serif text-xl text-foreground mb-3">Intellectual Property</h2>
            <p className="mb-3">
              Once you purchase a letter, you're free to use it personally—print it, share it 
              with friends, frame it on your wall. However:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>You may not resell or commercially distribute your letter</li>
              <li>The Eunice brand, character, and generation system remain our intellectual property</li>
              <li>You may not use our service to generate content for commercial purposes</li>
            </ul>
          </section>

          <section className="opacity-0 animate-fade-up animation-delay-300">
            <h2 className="font-serif text-xl text-foreground mb-3">Limitation of Liability</h2>
            <p className="mb-3">
              We're trying to create something helpful and delightful, but we can't guarantee 
              everything will be perfect. Specifically, we're not responsible if:
            </p>
            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li>A recommended restaurant has closed or moved</li>
              <li>Information about a location turns out to be inaccurate</li>
              <li>Your travel plans don't work out as hoped</li>
              <li>Technical issues delay delivery of your letter</li>
            </ul>
            <p>Our total liability is limited to the amount you paid for your letter ($1.99).</p>
          </section>

          <section className="opacity-0 animate-fade-up animation-delay-300">
            <h2 className="font-serif text-xl text-foreground mb-3">Our Rights</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>We may refuse service to anyone, for any reason</li>
              <li>We may update these terms at any time (changes are effective immediately)</li>
              <li>We may modify or discontinue the service at any time</li>
            </ul>
          </section>

          <section className="opacity-0 animate-fade-up animation-delay-300">
            <h2 className="font-serif text-xl text-foreground mb-3">Governing Law</h2>
            <p>
              These terms are governed by and construed in accordance with applicable laws. 
              For EU customers, nothing in these terms affects your statutory consumer rights.
            </p>
          </section>

          <section className="opacity-0 animate-fade-up animation-delay-300">
            <h2 className="font-serif text-xl text-foreground mb-3">Questions?</h2>
            <p className="mb-4">If you have any questions about these terms, we'd love to hear from you:</p>
            <a href="mailto:letters@eunice.world" className="text-primary hover:underline">
              letters@eunice.world
            </a>
          </section>

          <section className="pt-6 border-t border-tan/30">
            <p className="text-sm text-muted-foreground">
              See also our{" "}
              <Link to="/privacy" className="text-primary hover:underline">
                Privacy Policy
              </Link>
              {" "}for information about how we handle your data.
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-tan/30 opacity-0 animate-fade-up animation-delay-300">
          <Link to="/">
            <Button variant="eunice-outline" size="lg">
              ← Back to Home
            </Button>
          </Link>
        </div>
      </article>
    </PageLayout>
  );
};

export default Terms;
